import React, { useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const REPORTS = [
  { key: 'property-vs-target', label: 'Property-wise Revenue vs Target', filters: ['year', 'month'] },
  { key: 'daily-revenue',      label: 'Daily Revenue Report',             filters: ['from', 'to'] },
  { key: 'monthly-achievement',label: 'Monthly Achievement Summary',      filters: ['year'] },
];

function fmtMoney(v, code = 'INR') {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: code, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
}

function StatusBadge({ status }) {
  const map = {
    MET:    { bg: '#dcfce7', color: '#166534' },
    NEAR:   { bg: '#fef3c7', color: '#92400e' },
    BEHIND: { bg: '#fee2e2', color: '#991b1b' },
    NO_TARGET: { bg: '#f3f4f6', color: '#475569' },
    FUTURE: { bg: '#e0e7ff', color: '#3730a3' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#475569' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px',
      fontSize: 11, fontWeight: 700 }}>{status || '—'}</span>
  );
}

function PmsRevenueReports({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const today = new Date();
  const yyyymmdd = today.toISOString().slice(0, 10);
  const monthStart = `${yyyymmdd.slice(0, 8)}01`;

  const [reportKey, setReportKey] = useState('daily-revenue');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(yyyymmdd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const report = REPORTS.find((r) => r.key === reportKey);
  const currency = selectedProperty?.currency_code || 'INR';

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const buildQuery = (format) => {
    const p = new URLSearchParams();
    if (format) p.set('format', format);
    if (report.filters.includes('year')) p.set('year', String(year));
    if (report.filters.includes('month')) p.set('month', String(month));
    if (report.filters.includes('from')) p.set('from', from);
    if (report.filters.includes('to')) p.set('to', to);
    return p.toString();
  };

  const run = async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await fetch(`/api/v1/revenue-target/reports/${reportKey}?${buildQuery()}`,
        { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load');
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch(`/api/v1/revenue-target/reports/${reportKey}?${buildQuery(format)}`,
        { headers: authHeaders() });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `Failed (${res.status})`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `report.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 20 }}><div className="error-banner">Please select a property first.</div></div>;
  }

  // ─ Render helpers per report ───────────────────────────────────────────
  function renderRows() {
    if (!data) return null;
    if (reportKey === 'property-vs-target') {
      const rows = data.rows || [];
      if (!rows.length) return <div style={{ padding: 16, color: '#6b7280' }}>No properties found.</div>;
      return (
        <table style={tableStyle}>
          <thead>
            <tr style={theadRow}>
              <th style={th}>Property</th>
              <th style={th}>Target</th>
              <th style={th}>MTD Revenue</th>
              <th style={th}>Achievement %</th>
              <th style={th}>Remaining</th>
              <th style={th}>Occupancy %</th>
              <th style={th}>Budgeted Exp</th>
              <th style={th}>Incurred Exp</th>
              <th style={th}>Profit MTD</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.property_id} style={tbodyRow}>
                <td style={td}>{r.property_name} <span style={{ color: '#94a3b8' }}>{r.property_code || ''}</span></td>
                <td style={tdR}>{fmtMoney(r.target_amount, r.currency_code)}</td>
                <td style={tdR}>{fmtMoney(r.mtd_revenue, r.currency_code)}</td>
                <td style={{ ...tdR, color: r.achievement_pct >= 100 ? '#16a34a' : r.achievement_pct >= 80 ? '#2563eb' : '#dc2626' }}>
                  {r.achievement_pct == null ? '—' : `${r.achievement_pct.toFixed(1)}%`}
                </td>
                <td style={tdR}>{fmtMoney(r.remaining, r.currency_code)}</td>
                <td style={tdR}>{r.occupancy_pct?.toFixed(1)}%</td>
                <td style={tdR}>{fmtMoney(r.budgeted_expenses, r.currency_code)}</td>
                <td style={tdR}>{fmtMoney(r.incurred_expenses, r.currency_code)}</td>
                <td style={{ ...tdR, color: r.profit_mtd >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {fmtMoney(r.profit_mtd, r.currency_code)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (reportKey === 'daily-revenue') {
      const rows = data.rows || [];
      const tot = data.totals || {};
      const cur = data.currency_code || currency;
      if (!rows.length) return <div style={{ padding: 16, color: '#6b7280' }}>No data for selected range.</div>;
      return (
        <table style={tableStyle}>
          <thead>
            <tr style={theadRow}>
              <th style={th}>Date</th>
              <th style={th}>Room</th>
              <th style={th}>POS F&amp;B</th>
              <th style={th}>POS Bar</th>
              <th style={th}>Additional</th>
              <th style={th}>Total</th>
              <th style={th}>Occupancy %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date} style={tbodyRow}>
                <td style={td}>{r.date}</td>
                <td style={tdR}>{fmtMoney(r.room, cur)}</td>
                <td style={tdR}>{fmtMoney(r.pos_fnb, cur)}</td>
                <td style={tdR}>{fmtMoney(r.pos_bar, cur)}</td>
                <td style={tdR}>{fmtMoney(r.additional, cur)}</td>
                <td style={{ ...tdR, fontWeight: 700 }}>{fmtMoney(r.total, cur)}</td>
                <td style={tdR}>{r.occupancy_pct?.toFixed(1)}%</td>
              </tr>
            ))}
            <tr style={{ background: '#f8fafc', borderTop: '2px solid #cbd5e0', fontWeight: 700 }}>
              <td style={td}>TOTAL</td>
              <td style={tdR}>{fmtMoney(tot.room, cur)}</td>
              <td style={tdR}>{fmtMoney(tot.pos_fnb, cur)}</td>
              <td style={tdR}>{fmtMoney(tot.pos_bar, cur)}</td>
              <td style={tdR}>{fmtMoney(tot.additional, cur)}</td>
              <td style={tdR}>{fmtMoney(tot.total, cur)}</td>
              <td style={tdR}></td>
            </tr>
          </tbody>
        </table>
      );
    }
    if (reportKey === 'monthly-achievement') {
      const rows = data.rows || [];
      const cur = data.currency_code || currency;
      return (
        <table style={tableStyle}>
          <thead>
            <tr style={theadRow}>
              <th style={th}>Month</th>
              <th style={th}>Target</th>
              <th style={th}>Revenue</th>
              <th style={th}>Achievement %</th>
              <th style={th}>Remaining</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} style={tbodyRow}>
                <td style={td}>{r.month_label}</td>
                <td style={tdR}>{r.target_amount ? fmtMoney(r.target_amount, cur) : '—'}</td>
                <td style={tdR}>{fmtMoney(r.revenue, cur)}</td>
                <td style={{ ...tdR, color: r.achievement_pct >= 100 ? '#16a34a' : r.achievement_pct >= 80 ? '#2563eb' : r.achievement_pct == null ? '#94a3b8' : '#dc2626' }}>
                  {r.achievement_pct == null ? '—' : `${r.achievement_pct.toFixed(1)}%`}
                </td>
                <td style={tdR}>{r.target_amount ? fmtMoney(r.remaining, cur) : '—'}</td>
                <td style={td}><StatusBadge status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return null;
  }

  const inputStyle = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };

  return (
    <div style={{ padding: 16, fontFamily: 'inherit' }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
        Expense &amp; Profitability <span style={{ color: '#cbd5e0' }}>›</span>{' '}
        <span style={{ color: 'var(--theme-primary)', fontWeight: 600 }}>Revenue Reports</span>
      </div>
      <h3 style={{ margin: '4px 0 14px', color: 'var(--theme-primary)' }}>Revenue Reports</h3>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '230px 1fr', gap: 16 }}>
        {/* Left rail — report picker */}
        <div>
          {REPORTS.map((r) => (
            <button key={r.key} onClick={() => { setReportKey(r.key); setData(null); }}
              style={{
                width: '100%', textAlign: 'left',
                padding: '10px 12px', marginBottom: 6, border: '1px solid',
                borderColor: reportKey === r.key ? 'var(--theme-primary)' : '#e5e7eb',
                background: reportKey === r.key ? '#eff6ff' : '#fff',
                color: reportKey === r.key ? 'var(--theme-primary)' : 'var(--theme-text)',
                borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}>
              {r.label}
            </button>
          ))}
        </div>

        <div>
          {/* Filter strip */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12,
            padding: '10px 14px', background: '#f8faff', border: '1px solid #d7e3f4', borderRadius: 8 }}>
            {report.filters.includes('year') && (
              <div>
                <div style={lbl}>Year</div>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={inputStyle}>
                  {[today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}
            {report.filters.includes('month') && (
              <div>
                <div style={lbl}>Month</div>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={inputStyle}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2026, m - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {report.filters.includes('from') && (
              <div>
                <div style={lbl}>From</div>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
              </div>
            )}
            {report.filters.includes('to') && (
              <div>
                <div style={lbl}>To</div>
                <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
              </div>
            )}
            <button onClick={run} disabled={loading}
              style={{ padding: '7px 16px', background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              {loading ? 'Running…' : 'Run'}
            </button>
            {data && (
              <>
                <button onClick={() => download('csv')} style={{ ...btnSecondary, background: '#16a34a' }}>
                  Download CSV
                </button>
                <button onClick={() => download('pdf')} style={{ ...btnSecondary, background: '#7c3aed' }}>
                  Download PDF
                </button>
              </>
            )}
          </div>

          {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

          {!data && !loading && (
            <div style={{ padding: 24, color: '#6b7280', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              Choose filters and click Run.
            </div>
          )}

          {data && (
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto' }}>
              {renderRows()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 };
const btnSecondary = {
  padding: '7px 14px', color: '#fff', border: 'none', borderRadius: 6,
  fontWeight: 600, cursor: 'pointer', fontSize: 13,
};
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const theadRow = { background: 'var(--theme-primary)', color: '#fff' };
const tbodyRow = { borderTop: '1px solid #f1f5f9' };
const th = { padding: '8px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '7px 10px' };
const tdR = { padding: '7px 10px', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' };

export default PmsRevenueReports;
